import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

function BatchList() {
  const navigate = useNavigate();
  const { inventoryId } = useParams();

  const [batches, setBatches] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [formData, setFormData] = useState({
    batchNumber: '',
    quantity: '',
    manufactureDate: '',
    expiryDate: '',
    purchaseDate: '',
    supplierName: '',
    costPrice: '',
    status: 'Active',
    notes: ''
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    fetchInventory();
    fetchBatches();
  }, [inventoryId]);

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/inventory/${inventoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data);
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError('Failed to load inventory data');
    }
  };

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/batches/inventory/${inventoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching batches:', err);
      setError('Failed to load batches');
      setLoading(false);
    }
  };

  const generateBatchNumber = () => {
    const medicineName = inventory?.medicine?.name || 'MED';
    // Take first 3 letters of each word, uppercase, max 6 chars
    const code = medicineName
      .split(/\s+/)
      .map(w => w.slice(0, 3).toUpperCase())
      .join('')
      .slice(0, 6);
    const dateStr = dayjs().format('YYYYMMDD');
    // Next sequence = existing batches count + 1
    const seq = String(batches.length + 1).padStart(3, '0');
    return `BATCH-${code}-${dateStr}-${seq}`;
  };

  const handleOpenDialog = (batch = null) => {
    if (batch) {
      setEditingBatch(batch);
      setFormData({
        batchNumber: batch.batchNumber,
        quantity: batch.quantity,
        manufactureDate: batch.manufactureDate ? dayjs(batch.manufactureDate).format('YYYY-MM-DD') : '',
        expiryDate: dayjs(batch.expiryDate).format('YYYY-MM-DD'),
        purchaseDate: batch.purchaseDate ? dayjs(batch.purchaseDate).format('YYYY-MM-DD') : '',
        supplierName: batch.supplierName || '',
        costPrice: batch.costPrice || '',
        status: batch.status,
        notes: batch.notes || ''
      });
    } else {
      setEditingBatch(null);
      setFormData({
        batchNumber: generateBatchNumber(),
        quantity: '',
        manufactureDate: '',
        expiryDate: '',
        purchaseDate: dayjs().format('YYYY-MM-DD'),
        supplierName: '',
        costPrice: '',
        status: 'Active',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBatch(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        inventoryId: parseInt(inventoryId),
        quantity: parseInt(formData.quantity),
        costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null,
        manufactureDate: formData.manufactureDate || null,
        purchaseDate: formData.purchaseDate || null
      };

      if (editingBatch) {
        await axios.put(`${API_URL}/batches/${editingBatch.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/batches`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      handleCloseDialog();
      fetchBatches();
      fetchInventory();
    } catch (err) {
      console.error('Error saving batch:', err);
      setError(err.response?.data?.error || 'Failed to save batch');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/batches/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBatches();
      fetchInventory();
    } catch (err) {
      console.error('Error deleting batch:', err);
      setError('Failed to delete batch');
    }
  };

  const handleStatusChange = async (batchId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/batches/${batchId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBatches();
      fetchInventory();
    } catch (err) {
      console.error('Error updating batch status:', err);
      setError('Failed to update batch status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Expired': return 'error';
      case 'Recalled': return 'warning';
      case 'Used': return 'default';
      default: return 'default';
    }
  };

  const isExpiringSoon = (expiryDate) => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return new Date(expiryDate) <= thirtyDaysFromNow && new Date(expiryDate) >= new Date();
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <Typography>Loading batches...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/inventory')}
            sx={{ mb: 2 }}
          >
            Back to Inventory
          </Button>
          <Typography variant="h4" component="h1">
            Batch Management
          </Typography>
          {inventory && (
            <Typography variant="subtitle1" color="textSecondary">
              {inventory.medicine?.name} - Total Stock: {inventory.totalStock}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Batch
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Batches Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Batch Number</strong></TableCell>
              <TableCell align="center"><strong>Quantity</strong></TableCell>
              <TableCell><strong>Manufacture Date</strong></TableCell>
              <TableCell><strong>Expiry Date</strong></TableCell>
              <TableCell><strong>Purchase Date</strong></TableCell>
              <TableCell><strong>Supplier</strong></TableCell>
              <TableCell align="center"><strong>Cost Price</strong></TableCell>
              <TableCell align="center"><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No batches found
                </TableCell>
              </TableRow>
            ) : (
              batches.map((batch) => (
                <TableRow
                  key={batch.id}
                  sx={{
                    backgroundColor: isExpired(batch.expiryDate) && batch.status === 'Active'
                      ? '#ffebee'
                      : isExpiringSoon(batch.expiryDate) && batch.status === 'Active'
                        ? '#fff3cd'
                        : 'inherit'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {batch.batchNumber}
                      {isExpiringSoon(batch.expiryDate) && batch.status === 'Active' && (
                        <Tooltip title="Expiring soon">
                          <WarningIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                      {isExpired(batch.expiryDate) && batch.status === 'Active' && (
                        <Tooltip title="Expired">
                          <WarningIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center"><strong>{batch.quantity}</strong></TableCell>
                  <TableCell>
                    {batch.manufactureDate
                      ? new Date(batch.manufactureDate).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {new Date(batch.expiryDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {batch.purchaseDate
                      ? new Date(batch.purchaseDate).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>{batch.supplierName || '-'}</TableCell>
                  <TableCell align="center">
                    {batch.costPrice ? `₹${parseFloat(batch.costPrice).toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Select
                      value={batch.status}
                      onChange={(e) => handleStatusChange(batch.id, e.target.value)}
                      size="small"
                      sx={{ minWidth: 100 }}
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Expired">Expired</MenuItem>
                      <MenuItem value="Recalled">Recalled</MenuItem>
                      <MenuItem value="Used">Used</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(batch)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(batch.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Batch Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBatch ? 'Edit Batch' : 'Add New Batch'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Batch Number *"
                  name="batchNumber"
                  value={formData.batchNumber}
                  onChange={handleChange}
                  required
                  disabled={editingBatch !== null}
                  helperText={!editingBatch ? 'Auto-generated — you can edit if needed' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity *"
                  name="quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Manufacture Date"
                  name="manufactureDate"
                  type="date"
                  value={formData.manufactureDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expiry Date *"
                  name="expiryDate"
                  type="date"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Purchase Date"
                  name="purchaseDate"
                  type="date"
                  value={formData.purchaseDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  name="supplierName"
                  value={formData.supplierName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cost Price per Unit"
                  name="costPrice"
                  type="number"
                  value={formData.costPrice}
                  onChange={handleChange}
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Expired">Expired</MenuItem>
                    <MenuItem value="Recalled">Recalled</MenuItem>
                    <MenuItem value="Used">Used</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingBatch ? 'Update' : 'Add'} Batch
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}

export default BatchList;
